import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, DatabaseBackup, FileSpreadsheet, Eraser, Landmark, ShieldCheck, Car, FileDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AdminPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [cleaning, setCleaning] = useState(false);
  const [recat, setRecat] = useState(false);
  const [racDocs, setRacDocs] = useState(false);
  const [hrDocs, setHrDocs] = useState(false);
  const [internalRecat, setInternalRecat] = useState(false);
  const [normPartners, setNormPartners] = useState(false);
  const [access, setAccess] = useState({ users: [], me: "" });
  const [newEmail, setNewEmail] = useState("");

  const load = () => {
    api.get("/access/users").then((r) => setAccess(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/postavke" replace />;

  const recategorizePublic = async () => {
    setRecat(true);
    try {
      const r = await api.post("/maintenance/recategorize-public");
      const total = r.data.merged + r.data.recategorized;
      toast.success(total > 0 ? `Javni prihodi: ${r.data.merged} spojeno iz stare kategorije, ${r.data.recategorized} prepoznato po ključnim riječima` : "Nema promjena — sve je već kategorizirano");
    } catch {
      toast.error("Greška pri kategorizaciji");
    } finally {
      setRecat(false);
    }
  };

  const cleanCounterparties = async () => {
    setCleaning(true);
    try {
      const r = await api.post("/maintenance/clean-counterparties");
      toast.success(r.data.updated > 0 ? `Ažurirano ${r.data.updated} transakcija (provjereno ${r.data.checked})` : "Svi nazivi su već čisti — nema promjena");
    } catch {
      toast.error("Greška pri čišćenju naziva");
    } finally {
      setCleaning(false);
    }
  };

  const downloadBackup = async (format) => {
    const r = await api.get("/backup", { params: { format }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cashline-backup.${format === "json" ? "json" : "xlsx"}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const normalizePartners = async () => {
    setNormPartners(true);
    try {
      const r = await api.post("/maintenance/normalize-partners");
      const total = r.data.fizicka_osoba + r.data.polog_pazara;
      toast.success(total > 0 ? `Objedinjeno: ${r.data.fizicka_osoba} stavki → 'Fizička osoba', ${r.data.polog_pazara} stavki → 'Polog pazara'` : "Nema promjena — nazivi su već objedinjeni");
    } catch {
      toast.error("Greška pri objedinjavanju naziva");
    } finally {
      setNormPartners(false);
    }
  };

  const recategorizeInternal = async () => {
    setInternalRecat(true);
    try {
      const r = await api.post("/maintenance/recategorize-internal");
      const total = r.data.transfers + r.data.founders;
      toast.success(total > 0 ? `Prekategorizirano: ${r.data.transfers} transfera između VTZ računa i ${r.data.founders} isplata osnivačima` : "Nema promjena — sve je već ispravno označeno");
    } catch {
      toast.error("Greška pri kategorizaciji");
    } finally {
      setInternalRecat(false);
    }
  };

  const downloadRacDocs = async () => {
    setRacDocs(true);
    try {
      const r = await api.get("/rentacar-docs/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Cashline-RentACar-API.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Dokumentacija preuzeta — sadrži produkcijski API ključ, dijelite je samo s kolegom");
    } catch {
      toast.error("Greška pri generiranju dokumentacije");
    } finally {
      setRacDocs(false);
    }
  };

  const downloadHrDocs = async () => {
    setHrDocs(true);
    try {
      const r = await api.get("/hr-docs/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "MasterFB-HR-API-dokumentacija.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Dokumentacija preuzeta — sadrži produkcijski API ključ za Master F&B HR most");
    } catch {
      toast.error("Greška pri generiranju dokumentacije");
    } finally {
      setHrDocs(false);
    }
  };

  const normEmail = (e) => {
    const s = (e || "").trim().toLowerCase();
    const [l, d] = s.split("@");
    return d === "gmail.com" || d === "googlemail.com" ? l.replaceAll(".", "") + "@gmail.com" : s;
  };

  const addAccess = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    try {
      await api.post("/access/users", { email: newEmail.trim() });
      toast.success(`Pristup dodan: ${newEmail.trim()}`);
      setNewEmail("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška");
    }
  };

  const removeAccess = async (u) => {
    if (!window.confirm(`Ukloniti pristup za ${u.email}? Ta osoba se odmah odjavljuje i više se ne može prijaviti.`)) return;
    try {
      await api.delete(`/access/users/${u.id}`);
      toast.success(`Pristup uklonjen: ${u.email}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška");
    }
  };

  const compVal = (u) => {
    const c = u.companies || ["vtz", "masterfb"];
    if (c.includes("vtz") && c.includes("masterfb")) return "vtz,masterfb";
    return c.includes("masterfb") ? "masterfb" : "vtz";
  };
  const setUserCompanies = async (u, val) => {
    try {
      await api.put(`/access/users/${u.id}/companies`, { companies: val.split(",") });
      toast.success(`Pristup tvrtkama ažuriran za ${u.email}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri spremanju pristupa");
    }
  };

  return (
    <div className="space-y-8 fade-up" data-testid="admin-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">Samo administrator</p>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Administracija</h1>
      </div>

      <section className="space-y-4" data-testid="access-section">
        <h2 className="font-heading text-xl font-semibold text-[#0F172A] flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#0D9488]" /> Pristup aplikaciji
        </h2>
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-5 max-w-2xl space-y-4">
          <p className="text-sm text-[#475569]">
            Samo navedeni Google računi mogu se prijaviti. Uklanjanjem se osoba odmah odjavljuje. Vlastiti pristup ne možete ukloniti.
          </p>
          <div className="divide-y divide-[#F1F5F9]" data-testid="access-users-list">
            {access.users.map((u) => {
              const isMe = normEmail(u.email) === access.me;
              return (
                <div key={u.id} data-testid={`access-user-${u.email}`} className="flex items-center gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#0F172A] truncate">
                      {u.email}
                      {isMe && <Badge className="ml-2 rounded-sm text-[10px] bg-[#0D9488]/10 text-[#0D9488] hover:bg-[#0D9488]/10">vi</Badge>}
                    </p>
                    <p className="text-xs text-[#64748B]">dodao/la: {u.added_by}</p>
                  </div>
                  <select
                    data-testid={`access-companies-${u.email}`}
                    value={compVal(u)}
                    onChange={(e) => setUserCompanies(u, e.target.value)}
                    className="text-xs border border-[#CBD5E1] rounded-sm px-2 py-1.5 bg-white text-[#0F172A] cursor-pointer shrink-0"
                  >
                    <option value="vtz,masterfb">Obje tvrtke</option>
                    <option value="vtz">Samo VTZ</option>
                    <option value="masterfb">Samo Master F&B</option>
                  </select>
                  {!isMe && (
                    <button
                      data-testid={`access-remove-${u.email}`}
                      onClick={() => removeAccess(u)}
                      title="Ukloni pristup"
                      className="p-2 text-[#64748B] hover:text-[#DC2626] transition-colors duration-200 shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <form onSubmit={addAccess} className="flex gap-2">
            <Input
              data-testid="access-email-input"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="novi.korisnik@gmail.com"
            />
            <Button data-testid="access-add-btn" type="submit" className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm shrink-0">
              <Plus className="w-4 h-4 mr-2" /> Dodaj pristup
            </Button>
          </form>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Sigurnosna kopija podataka</h2>
        <p className="text-sm text-[#475569] max-w-2xl">Preuzmite kompletnu kopiju svih poslovnih podataka (transakcije, planovi, fiksni troškovi, banke, kategorije, bilješke). Preporučujemo povremeno preuzimanje kopije.</p>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="backup-json-btn"
            onClick={() => downloadBackup("json")}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-[#0D9488] hover:bg-[#0F766E] text-white text-sm font-medium transition-colors duration-200"
          >
            <DatabaseBackup className="w-4 h-4" /> Preuzmi JSON kopiju
          </button>
          <button
            data-testid="backup-excel-btn"
            onClick={() => downloadBackup("excel")}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-white border border-[#CBD5E1] hover:bg-[#F1F5F9] text-[#0F172A] text-sm font-medium transition-colors duration-200"
          >
            <FileSpreadsheet className="w-4 h-4" /> Preuzmi Excel kopiju
          </button>
        </div>
      </section>

      <section className="space-y-4" data-testid="rentacar-docs-section">
        <h2 className="font-heading text-xl font-semibold text-[#0F172A] flex items-center gap-2">
          <Car className="w-5 h-5 text-[#0D9488]" /> Rent a Car integracija (API most)
        </h2>
        <p className="text-sm text-[#475569] max-w-2xl">
          PDF dokumentacija za kolegu koji spaja Rent a Car aplikaciju s Cash Flowom: produkcijski API ključ, adrese, svi endpointi s primjerima,
          pravila storna i checklist za produkciju. <b className="text-[#DC2626]">Dokument sadrži produkcijski ključ — dijelite ga isključivo s osobom koja radi integraciju.</b>
          {" "}Nakon Deploya preuzmite ga s vtz-cf.com kako bi ključ sigurno vrijedio i u produkciji.
        </p>
        <button
          data-testid="rentacar-docs-btn"
          onClick={downloadRacDocs}
          disabled={racDocs}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-50 text-white text-sm font-medium transition-colors duration-200"
        >
          <FileDown className="w-4 h-4" /> {racDocs ? "Generiranje..." : "Preuzmi API dokumentaciju (PDF)"}
        </button>
      </section>

      <section className="space-y-4" data-testid="hr-docs-section">
        <h2 className="font-heading text-xl font-semibold text-[#0F172A] flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#0D9488]" /> HR most za Master F&B (API)
        </h2>
        <p className="text-sm text-[#475569] max-w-2xl">
          PDF dokumentacija za spajanje VTZ HR aplikacije s Cash Flowom tvrtke <b>Master F&B BH</b>: zaseban API ključ (odvojen od VTZ ključa),
          endpointi za čitanje radnika/isplata i slanje ugovornih plaća kao fiksnih stavki.
          <b className="text-[#DC2626]"> Dokument sadrži produkcijski ključ — dijelite ga isključivo s osobom koja radi integraciju.</b>
        </p>
        <button
          data-testid="hr-docs-btn"
          onClick={downloadHrDocs}
          disabled={hrDocs}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-50 text-white text-sm font-medium transition-colors duration-200"
        >
          <FileDown className="w-4 h-4" /> {hrDocs ? "Generiranje..." : "Preuzmi HR API dokumentaciju (PDF)"}
        </button>
      </section>

      <section className="space-y-4">
        <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Čišćenje naziva primatelja</h2>
        <p className="text-sm text-[#475569] max-w-2xl">
          Ako nazivi primatelja iz starijih uvoza sadrže naziv banke i broj računa (npr. "UNION BANKA DD SARAJEVO 1020500000106698 JP Elektroprivreda"),
          ovim postupkom se banka i račun odvajaju u svoje kolone, a ostaje samo stvarni naziv dobavljača/platitelja. Novi uvozi se čiste automatski.
        </p>
        <button
          data-testid="clean-counterparties-btn"
          onClick={cleanCounterparties}
          disabled={cleaning}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-50 text-white text-sm font-medium transition-colors duration-200"
        >
          <Eraser className="w-4 h-4" /> {cleaning ? "Čišćenje..." : "Parsiraj nazive primatelja"}
        </button>
      </section>

      <section className="space-y-4">
        <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Interni transferi i osnivači</h2>
        <p className="text-sm text-[#475569] max-w-2xl">
          VTZ d.o.o. nije kupac ni dobavljač — uplate/isplate gdje je druga strana VTZ označavaju se kao "Transfer između računa".
          Isplate osnivačima (Franjo Vučić, Robert Rožić — sa ili bez dijakritika i grada; za Master F&B: Master FB Zagreb) označavaju se kategorijom "Osnivači".
          Novi uvozi se označavaju automatski; ovim postupkom se prekategoriziraju postojeće transakcije.
        </p>
        <button
          data-testid="recategorize-internal-btn"
          onClick={recategorizeInternal}
          disabled={internalRecat}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-50 text-white text-sm font-medium transition-colors duration-200"
        >
          <Eraser className="w-4 h-4" /> {internalRecat ? "Kategoriziranje..." : "Prekategoriziraj transfere i osnivače"}
        </button>
      </section>

      <section className="space-y-4">
        <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Objedinjavanje naziva partnera</h2>
        <p className="text-sm text-[#475569] max-w-2xl">
          Sve varijante "Kupac građanin" preimenuju se u "Fizička osoba", a sve varijante pologa pazara u "Polog pazara"
          (u Transakcijama uz svaki polog piše u koju je banku uplaćen). Isti partner s adresom i bez nje (npr. BASIC DARKO)
          ili s tipfelerom (Premier World Sport, Wiener osiguranje) grupira se kao jedan u Partnerima. Novi uvozi se objedinjuju automatski;
          ovim postupkom se srede postojeće transakcije.
        </p>
        <button
          data-testid="normalize-partners-btn"
          onClick={normalizePartners}
          disabled={normPartners}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-50 text-white text-sm font-medium transition-colors duration-200"
        >
          <Eraser className="w-4 h-4" /> {normPartners ? "Objedinjavanje..." : "Objedini nazive partnera"}
        </button>
      </section>

      <section className="space-y-4">
        <h2 className="font-heading text-xl font-semibold text-[#0F172A]">Javni prihodi i uplate u budžet</h2>
        <p className="text-sm text-[#475569] max-w-2xl">
          Sve uplate u budžet i javne prihode (porezi, doprinosi, PIO/MIO, zdravstveno, zapošljavanje, trezor, proračun, općina/kanton/federacija, Porezna uprava, UIO)
          automatski se svrstavaju u kategoriju "Javni prihodi". Ovim postupkom se postojeće transakcije prekategoriziraju, a stara kategorija "doprinosi i porezi" spaja se u novu.
        </p>
        <button
          data-testid="recategorize-public-btn"
          onClick={recategorizePublic}
          disabled={recat}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-50 text-white text-sm font-medium transition-colors duration-200"
        >
          <Landmark className="w-4 h-4" /> {recat ? "Kategoriziranje..." : "Prekategoriziraj javne prihode"}
        </button>
      </section>
    </div>
  );
}
